import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { SideNav } from "./SideNav";
import { MobileAppBar } from "./MobileAppBar";
import { NavDrawer } from "./NavDrawer";
import { AppFooter } from "./AppFooter";
import { Breadcrumb } from "./Breadcrumb";
import { ShellChromeContext } from "./shellChrome";
import { useBreakpoint } from "../lib/useBreakpoint";
import { findNavByPath } from "../lib/navigation";
import styles from "./AppShell.module.css";

export interface AppShellProps {
  children: ReactNode;
  currentPath: string;
  bare?: boolean;
  onSignOut?: () => void;
  onNavigate?: (href: string) => void;
  footerHidden?: boolean;
}

export function AppShell({
  children,
  currentPath,
  bare = false,
  onSignOut,
  onNavigate,
  footerHidden = false,
}: AppShellProps): ReactElement {
  const { isMobile } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [back, setBack] = useState<(() => void) | null>(null);
  const [chromeHidden, setChromeHidden] = useState(false);

  const registerBack = useCallback((handler: (() => void) | null) => {
    setBack(() => handler);
  }, []);
  const chrome = useMemo(
    () => ({ setBack: registerBack, setHidden: setChromeHidden }),
    [registerBack],
  );

  useEffect(() => {
    setDrawerOpen(false);
    setBack(null);
    // chromeHidden is owned by the view that hides it (it clears on unmount);
    // resetting here would clobber a view that hides chrome on first mount,
    // e.g. the docs opened straight into ?fullscreen from a shared link.
  }, [currentPath]);

  if (bare) {
    return <div className={styles.bare}>{children}</div>;
  }

  const signOut = onSignOut ?? (() => {});
  const footer = <AppFooter hidden={footerHidden} />;
  const content = <div className={styles.content}>{children}</div>;

  if (isMobile) {
    return (
      <ShellChromeContext.Provider value={chrome}>
        <div className={styles.mobile}>
          {chromeHidden ? null : (
            <MobileAppBar
              title={findNavByPath(currentPath)?.short ?? "PFDN"}
              currentPath={currentPath}
              onSignOut={signOut}
              onMenu={() => setDrawerOpen(true)}
              onAccount={() => onNavigate?.("/app/account")}
              showBack={!!back}
              onBack={() => back?.()}
            />
          )}
          <NavDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            currentPath={currentPath}
            onSignOut={signOut}
            onNavigate={onNavigate}
          />
          <main className={styles.main}>
            {content}
            {chromeHidden ? null : footer}
          </main>
        </div>
      </ShellChromeContext.Provider>
    );
  }

  return (
    <ShellChromeContext.Provider value={chrome}>
      <div
        className={`${styles.desktop} ${chromeHidden ? styles.chromeHidden : ""}`}
      >
        {chromeHidden ? null : (
          <SideNav
            currentPath={currentPath}
            onSignOut={signOut}
            onNavigate={onNavigate}
          />
        )}
        <main className={styles.main}>
          {chromeHidden ? null : (
            <Breadcrumb path={currentPath} onNavigate={onNavigate} />
          )}
          {content}
          {chromeHidden ? null : footer}
        </main>
      </div>
    </ShellChromeContext.Provider>
  );
}
